import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// --- Interfaces ---
import type { IClient, IClients, ICnpjLookup } from '@interfaces/client';
import { createHttpParams } from '@utils/http-params.util';
import { normalizeSellerId } from '@utils/sellers.util';

export interface IGetClientsParams
	extends Partial<{
		search: string;
		inactive: boolean;
		viewClientsWithoutSeller: boolean;
		sellerId: number;
		tenantId: number;
		page: number;
		pageLimit: number;
	}> {}

@Injectable({
	providedIn: 'root',
})
export class ClientsService {
	constructor(private readonly _http: HttpClient) {}

	// --- GET endpoints ---
	getClients(queryParams?: IGetClientsParams) {
		return this._http.get<IClients>('clients', {
			params: createHttpParams(queryParams),
		});
	}

	getClientById(id: IClient['id']) {
		return this._http.get<IClient>(`clients/${id}`);
	}

	lookupCnpj(document: string) {
		return this._http.get<ICnpjLookup>(
			`clients/cnpj/${encodeURIComponent(document)}`
		);
	}

	// --- POST endpoints ---
	postClient(client: IClient) {
		const payload = this.normalizeClient(client);
		return this._http.post<IClient>(`clients`, payload);
	}

	createAddress(clientId: number, address: any) {
		return this._http.post<any>(`clients/${clientId}/addresses`, address);
	}

	// --- PUT endpoints ---
	updateClient(id: IClient['id'], client: IClient) {
		const payload = this.normalizeClient(client);
		return this._http.put<IClient>(`clients/${id}`, payload);
	}

	private normalizeClient(client: IClient): IClient {
		return {
			...client,
			sellerId: normalizeSellerId(client.sellerId) as any,
		};
	}

	// --- PATCH endpoints ---
	activateClient(id: IClient['id']) {
		return this._http.patch<IClient>(`clients/${id}/activate`, null);
	}

	inactivateClient(id: IClient['id']) {
		return this._http.patch<IClient>(`clients/${id}/inactivate`, null);
	}
}
